import { useMemo } from 'react';
import { v4 as uuid } from 'uuid';

import { flowNodeColor } from 'fable/flowEditor';
import { PhoneExtraData } from 'fable/flowEditor/components/flowManager/canvas/types/extraData';
import { Text } from 'flow-builder/node/text';
import { TextContent } from 'flow-builder/node/textContent';
import { TextEllipsis } from 'flow-builder/node/textEllipsis';
import { hexStringToNumber } from 'flow-builder/renderers/common/colors';
import type {
  GenericNodeAttributes,
  GenericNodeProps,
} from 'flow-builder/types/node';

import { DialInput as DialInputModal } from '../../modals/dialInput';

export type Announcement = {
  file: {
    author: string;
    filename: string;
    url: string;
  };
};

type Payload = {
  announcement: Announcement;
  end_key: null | '*' | '#';
  max_digits: null | number;
  next_node_id: string;
  variable_id: string;
};

type DialInputNodeAttributes = GenericNodeAttributes<Payload, PhoneExtraData>;
type DialInputNodeProps = GenericNodeProps<Payload, PhoneExtraData>;

const translatePayload: DialInputNodeAttributes['translatePayload'] = (
  node,
) => {
  const { next_node_id } = node.payload;

  return [
    {
      id: uuid(),
      label: 'arrow',
      connection: next_node_id || uuid(),
    },
  ];
};

const updateConnection: DialInputNodeAttributes['updateConnection'] = (
  node,
  _connection,
  connectionId,
) => {
  const payload = { ...node.payload, next_node_id: connectionId };

  return { ...node, payload };
};

const translateScopeVariables: DialInputNodeAttributes['translateScopeVariables'] =
  (node) => {
    const { variable_id } = node.payload;
    const grants = variable_id ? [variable_id] : [];

    return {
      grants,
      needs: [],
    };
  };

export const DialInputNodeAttributes: DialInputNodeAttributes = {
  iconName: 'phone',
  label: 'Dial input',
  helpText:
    'Allow customers to give input with their dial pad during a call (e.g. a PIN, ZIP code, or phone number).',
  color: hexStringToNumber(flowNodeColor.orange),
  translateScopeVariables,
  translatePayload,
  updateConnection,
  NodeContent: DialInputNode,
  ActionModal: DialInputModal,
};

export function DialInputNode({ node }: DialInputNodeProps): JSX.Element {
  const { payload } = node;
  const { variable_id } = payload;

  return (
    <TextContent>
      {useMemo(
        () => (
          <TextEllipsis>
            <Text weight="normal" text="Variable" />
            <Text weight="semibold" text={variable_id} />
          </TextEllipsis>
        ),
        [variable_id],
      )}
    </TextContent>
  );
}
